/**
 * Converts a time string like "1:06:09" or "04:20" to seconds.
 *
 * @param time The time string to convert.
 * @returns The number of seconds for the supplied time string.
 */
export function timeToSeconds(time: string) {
  if (!time) {
    return 0;
  }

  // HookSounds data sometimes returns times like: "3:20, 3:34". For those cases, just
  // use the first time and ignore everything else.
  time = time.split(',')[0];
  // Some tracks use ; or . as separator instead of :, replace it.
  time = time.replace(';', ':');
  time = time.replace('.', ':');
  // Remove all unexpected characters.
  time = time.replace(/[^0-9:]/g, '');

  // If the separator is missing, try to add it.
  if (time.indexOf(':') === -1) {
    if (time.length === 3) {
      time = time.slice(0, 1) + ':' + time.slice(1);
    } else if (time.length === 4) {
      time = time.slice(0, 2) + ':' + time.slice(2);
    }
  }

  const p = time.split(':');
  let s = 0;
  let m = 1;

  while (p.length > 0) {
    s += m * parseInt(p.pop() ?? '', 10);
    m *= 60;
  }
  return s;
}

/**
 * Converts a time value in seconds to a formatted string in the format of "MM:SS".
 * For example, a value of 90 seconds would be formatted as "01:30".
 *
 * @param seconds The number of seconds to convert to a formatted string.
 * @returns A string representation of the input time in the format of "MM:SS".
 */
export function secondsToTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(Math.floor(remainingSeconds)).padStart(2, '0');
  return `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Converts a progress value (0-1) to a time string in the format of "MM:SS".
 * Can receive a duration in seconds or a time string like "04:20".
 */
export function progressToTime(progress: number, duration: any) {
  if (typeof duration === 'string') {
    duration = timeToSeconds(duration);
  }
  return secondsToTime(progress * duration);
}

/**
 * Converts a time string like "04:20" or incorrectly formatted ones
 * like "420" or "4:2" to a formatted string in the format of "MM:SS".
 * @param time
 * @returns
 */
export function timeNormalized(time: string) {
  return secondsToTime(timeToSeconds(time));
}
